import { useState } from 'react'
import { RequireAtLeastOne } from 'type-fest'

type ItemBase = RequireAtLeastOne<
  {
    [key: string]: any
    id?: string
    uuid?: string
  },
  'id' | 'uuid'
>

type SelectionArgs<Item extends ItemBase> = {
  list: Array<Item>
  startSelected?: boolean
}

type SelectionValue<Item extends ItemBase> = {
  deselectAll: () => void
  onSelect: (item: Item) => void
  resetSelections: () => void
  selectAll: () => void
  selectedItems: Array<Item>
}

function useSelectObjectById<Item extends ItemBase>({
  list,
  startSelected = false,
}: SelectionArgs<Item>): SelectionValue<Item> {
  const [selectedItems, setSelectedItems] = useState<Array<Item>>(() =>
    startSelected ? list : []
  )

  const onSelect = ({ id }: Item) => {
    const selection = list.find((listItem) => listItem.id === id)
    if (!selection) return null

    if (!selectedItems.length) {
      return setSelectedItems([selection])
    }

    const selectedIndex = selectedItems.findIndex(
      (listItem) => listItem.id === id
    )
    if (selectedIndex !== -1) {
      return setSelectedItems(
        selectedItems.filter((listItem) => listItem.id !== id)
      )
    }

    return setSelectedItems([...selectedItems, selection])
  }

  const selectAll = () => setSelectedItems(list)
  const deselectAll = () => setSelectedItems([])
  const resetSelections = () => {
    const initialValue = startSelected ? list : []
    setSelectedItems(initialValue)
  }

  return {
    deselectAll,
    onSelect,
    resetSelections,
    selectAll,
    selectedItems,
  }
}

export { useSelectObjectById }
